import React from 'react';

import { MarkdownRenderer } from '@ergeon/core-components';

import { UnifiedDialogEntry } from '../../../../types';
import {
  renderFileAttachment,
  wrapContentWithAttachments,
} from '../components/AttachmentRenderer';
import {
  CallMessage,
  MissedCallMessage,
  AbandonedCallMessage,
  VoicemailMessage,
  EmailMessage,
  LveMessage,
  CustomerContextMessage,
  PropertyContextMessage,
  ToolkitContextMessage,
  LeadContextMessage,
  StatusChangeContextMessage,
  YelpMessage,
} from '../components/MessageTypes';
import { containsMarkdown } from '../components/InputTypes/components/RichTextInput/utils/pasteUtils';

import { getRawFileName, separateAttachments } from './attachmentProcessor';
import {
  autoLinkMarkdownUrls,
  ensureParagraphBreaks,
  MARKDOWN_ELEMENT_CLASSES,
  MARKDOWN_WRAPPER_CLASSES,
} from './markdownDisplay';
import { formatTextWithLinks } from './textFormatters';

// Helper function to render attachment URLs as links with truncated file names
// Used for internal notes where URLs may be embedded in the text
const renderAttachmentLinks = (text: string): React.ReactNode => {
  const lines = text.split('\n');
  const urlRegex = /^(https?:\/\/.+)$/;
  let renderedCount = 0;

  return (
    <>
      {lines.map((line, index) => {
        // Skip empty lines
        if (line.trim() === '') {
          return null;
        }

        const match = line.match(urlRegex);
        const needsBr = renderedCount > 0;
        renderedCount++;

        if (match) {
          const url = match[1];
          return (
            <React.Fragment key={index}>
              {needsBr && <br />}
              {renderFileAttachment(url, index)}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={index}>
            {needsBr && <br />}
            {line}
          </React.Fragment>
        );
      })}
    </>
  );
};

type ConvertOptions = {
  onReply?: (subject: string) => void;
};

// Helper function to convert API entry to MessageFeed format
export const convertEntryToMessage = (
  entry: UnifiedDialogEntry,
  options: ConvertOptions = {},
): {
  content: string | React.ReactNode;
  delivered_at: string;
  alignment: 'start' | 'center' | 'end';
  sender?: string;
  meta?: string;
  attachments?: string[];
} => {
  const { onReply } = options;
  const { entry_type, created_at, direction, from_phone, public_phone_label } = entry;

  // Determine message alignment for MessageFeed (can be overridden in specific cases)
  let alignment: 'start' | 'center' | 'end' = direction === 'inbound' ? 'start' : 'end';

  // Build content based on entry type
  let content: string | React.ReactNode = '';
  let meta = '';

  // For outbound messages, display line name with phone number if available
  let sender: string;
  if (direction === 'inbound') {
    sender = from_phone || 'Customer';
  } else {
    // Remove "Call - " prefix from public_phone_label if present
    const cleanLabel = public_phone_label?.replace(/^Call - /, '');

    if (cleanLabel && from_phone) {
      sender = `${cleanLabel} - ${from_phone}`;
    } else if (cleanLabel) {
      sender = cleanLabel;
    } else {
      sender = from_phone || 'Agent';
    }
  }

  let attachments: string[] = [];

  switch (entry_type) {
    case 'sms': {
      const { imageUrls, videoUrls, documentUrls } = separateAttachments(entry.media_urls || []);
      if (entry.carrier === 'yelp') {
        content = wrapContentWithAttachments(<YelpMessage text={entry.text || ''} />, imageUrls, videoUrls, documentUrls);
        // Add status indicator for Yelp
        if (entry.status === 'sending') {
          meta = 'Yelp • Sending...';
        } else if (entry.status === 'failed') {
          meta = 'Yelp (Failed)';
        } else {
          meta = 'Yelp';
        }
      } else {
        content = wrapContentWithAttachments(<span>{formatTextWithLinks(entry.text)}</span>, imageUrls, videoUrls, documentUrls);
        // Add status indicator
        if (entry.status === 'sending') {
          meta = 'SMS • Sending...';
        } else if (entry.status === 'failed') {
          meta = 'SMS (Failed)';
        } else {
          meta = 'SMS';
        }
      }
      attachments = imageUrls;
      break;
    }

    case 'sms_failed': {
      const { imageUrls, videoUrls, documentUrls } = separateAttachments(entry.media_urls || []);
      content = wrapContentWithAttachments(<span>{formatTextWithLinks(entry.text)}</span>, imageUrls, videoUrls, documentUrls);
      attachments = imageUrls;
      meta = 'SMS (Failed)';
      break;
    }

    case 'call_header':
      content = <CallMessage durationStr={entry.duration_str} recordingUrl={entry.recording_url} />;
      meta = 'Call';
      break;

    case 'call_segment':
      content = entry.text || '';
      sender = entry.speaker || 'Unknown';
      meta = 'Call transcript';
      // Customer on the left (start), Ergeon on the right (end)
      alignment = entry.speaker === 'Ergeon' ? 'end' : 'start';
      break;

    case 'call_missed':
      content = <MissedCallMessage />;
      meta = 'Missed Call';
      break;

    case 'call_abandoned':
      content = <AbandonedCallMessage />;
      meta = 'Abandoned Call';
      break;

    case 'voicemail_header':
      content = <VoicemailMessage durationStr={entry.duration_str} recordingUrl={entry.recording_url} />;
      meta = 'Voicemail';
      break;

    case 'voicemail_segment':
      content = entry.text || '';
      sender = entry.speaker || 'Unknown';
      meta = 'Voicemail transcript';
      break;

    case 'voicemail_simple':
      content = <VoicemailMessage durationStr={entry.duration_str} recordingUrl={entry.recording_url} />;
      meta = 'Voicemail';
      break;

    case 'internal_note': {
      const { imageUrls, videoUrls, documentUrls } = separateAttachments(entry.media_urls || []);
      const noteText = entry.text || '';

      // Filter out attachments that are already in the text (URLs embedded by buildMessageWithAttachments)
      // Use raw filename (not truncated) to ensure proper matching
      const imagesNotInText = imageUrls.filter((url) => !noteText.includes(getRawFileName(url)));
      const videosNotInText = videoUrls.filter((url) => !noteText.includes(getRawFileName(url)));
      const documentsNotInText = documentUrls.filter((url) => !noteText.includes(getRawFileName(url)));

      // Notes saved from RichTextInput are stored as markdown — render with MarkdownRenderer.
      // Older plain-text notes (no markdown formatting) fall back to the attachment-link renderer.
      const noteContent = containsMarkdown(noteText) ?
        (
          <div className={MARKDOWN_WRAPPER_CLASSES}>
            <MarkdownRenderer content={ensureParagraphBreaks(autoLinkMarkdownUrls(noteText))} elementClassNames={MARKDOWN_ELEMENT_CLASSES} />
          </div>
        ) :
        renderAttachmentLinks(noteText);
      content = wrapContentWithAttachments(noteContent, imagesNotInText, videosNotInText, documentsNotInText);
      attachments = imageUrls;
      sender = entry.created_by || 'Staff';
      // Add status indicator
      if (entry.status === 'sending') {
        meta = entry.is_system_generated ? 'System Note • Adding...' : 'Internal Note • Adding...';
      } else if (entry.status === 'failed') {
        meta = entry.is_system_generated ? 'System Note (Failed)' : 'Internal Note (Failed)';
      } else {
        meta = entry.is_system_generated ? 'System Note' : 'Internal Note';
      }
      alignment = 'center';
      break;
    }

    case 'email': {
      const { imageUrls, videoUrls, documentUrls } = separateAttachments(entry.media_urls || []);
      content = wrapContentWithAttachments(
        <EmailMessage
          direction={direction}
          emlFileUrl={entry.eml_file_url}
          entryId={entry.entry_id}
          fromEmail={entry.from_email}
          onReply={onReply}
          subject={entry.email_subject}
          text={entry.text}
          textFullLength={entry.text_full_length}
          toEmail={entry.to_email} />,
        imageUrls,
        videoUrls,
        documentUrls,
      );
      // Don't render attachments for emails - they're available via the email preview modal
      attachments = [];
      sender = direction === 'inbound' ? entry.from_email || 'Customer' : entry.from_email || 'Ergeon';
      // Add status indicator
      if (entry.status === 'sending') {
        meta = 'Email • Sending...';
      } else if (entry.status === 'failed') {
        meta = 'Email (Failed)';
      } else {
        meta = 'Email';
      }
      break;
    }

    case 'lve_header':
      content = <LveMessage durationStr={entry.duration_str} recordingUrls={entry.recording_urls} />;
      meta = 'LVE';
      break;

    case 'lve_segment':
      content = entry.text || '';
      sender = entry.speaker || 'Unknown';
      meta = 'LVE transcript';
      // Customer on the left (start), Ergeon on the right (end)
      alignment = entry.speaker === 'Ergeon' ? 'end' : 'start';
      break;

    case 'context':
      switch (entry.context_type) {
        case 'customer':
          content = (
            <CustomerContextMessage
              businessName={entry.context_data?.business_name}
              isCommercial={entry.context_data?.is_commercial}
              isKeyAccount={entry.context_data?.is_key_account}
              marketName={entry.context_data?.market_name}
              name={entry.context_data?.name}
              primaryEmail={entry.context_data?.primary_email}
              primaryPhone={entry.context_data?.primary_phone} />
          );
          meta = 'Customer Context';
          break;

        case 'property':
          content = (
            <PropertyContextMessage
              address={entry.context_data?.address}
              inHoa={entry.context_data?.in_hoa}
              lotArea={entry.context_data?.lot_area}
              propertyType={entry.context_data?.property_type}
              sqFootage={entry.context_data?.sq_footage}
              yearBuilt={entry.context_data?.year_built} />
          );
          meta = 'Property Context';
          break;

        case 'toolkit':
          content = (
            <ToolkitContextMessage
              maxFenceHeights={entry.context_data?.max_fence_heights}
              permitNeeded={entry.context_data?.permit_needed}
              zipCode={entry.context_data?.zip_code} />
          );
          meta = 'Toolkit Context';
          break;

        case 'lead':
          content = (
            <LeadContextMessage
              channel={entry.context_data?.channel}
              comment={entry.context_data?.comment}
              isComplete={entry.context_data?.is_complete}
              leadGid={entry.context_data?.lead_gid}
              orderId={entry.context_data?.order_id}
              services={entry.context_data?.services}
              tags={entry.context_data?.tags}
              utmCampaign={entry.context_data?.utm_campaign} />
          );
          meta = 'Lead Context';
          break;

        case 'status_change':
          content = <StatusChangeContextMessage adminLink={entry.admin_link} contextData={entry.context_data} />;
          meta = 'Status';
          break;

        default:
          content = entry.text || 'Unknown context type';
          meta = 'Context';
          break;
      }
      sender = 'System';
      alignment = 'center';
      break;

    default:
      content = 'Unknown message type';
      break;
  }

  return {
    content,
    delivered_at: created_at,
    alignment,
    sender,
    meta,
    attachments,
  };
};
